import { apiRequest } from '@/lib/api';

/**
 * Emma Session API Service
 * ADDITIVE LAYER - Does not modify existing behavior
 * 
 * BMad Team:
 * - Sarah (Lead Dev): Core implementation
 * - Marcus (Security): Auth token handling
 * - Quinn (Chaos): Fallback logic
 * - Priya (Performance): Retry strategy
 * 
 * BREAK NOTHING MANDATE: All methods return gracefully on failure
 */

export interface EmmaSessionData {
  sessionKey: string;
  sessionId: string;
  conversationHistory: Array<{
    role: 'user' | 'emma';
    content: string;
    timestamp: string;
    metadata?: Record<string, any>;
  }>;
  contextState: Record<string, any>;
  activeAgents: string[];
  lastMessageAt: string;
  expiresAt: string;
}

export interface CreateSessionResponse {
  session_key: string;
  session_id: string;
  expires_at: string;
}

export interface ApiResult<T> {
  success: boolean;
  data?: T;
  error?: string;
  fallbackUsed?: boolean;
}

// Feature flag check
const isBackendSessionEnabled = (): boolean => {
  try {
    const flags = localStorage.getItem('featureFlags');
    if (flags) {
      const parsed = JSON.parse(flags);
      return parsed['emma.backendSession'] === true;
    }
  } catch {
    // Ignore parse errors
  }
  return false; // Disabled by default - SAFE
};


// Base API call with timeout and error handling
const apiCall = async <T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResult<T>> => {
  // Check feature flag first
  if (!isBackendSessionEnabled()) {
    return { success: false, error: 'Backend sessions disabled', fallbackUsed: true };
  }
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 5000); // 5s timeout

  try {
    const response = await apiRequest(`/api/emma-sessions${endpoint}`, {
      ...options,
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    clearTimeout(timeout);

    if (!response.ok) {
      console.warn(`[EmmaSessionAPI] ${endpoint} returned ${response.status}`);
      return { success: false, error: `HTTP ${response.status}`, fallbackUsed: true };
    }

    const data = await response.json();
    return { success: true, data };
  } catch (err) {
    clearTimeout(timeout);
    const message = err instanceof Error ? err.message : 'Unknown error';
    console.warn(`[EmmaSessionAPI] ${endpoint} failed:`, message);
    return { success: false, error: message, fallbackUsed: true };
  }
};

/**
 * Create a new Emma session on the backend
 * Falls back gracefully if backend unavailable
 */
export const createSession = async (
  deviceId?: string,
  deviceType?: string
): Promise<ApiResult<CreateSessionResponse>> => {
  return apiCall<CreateSessionResponse>('/sessions', {
    method: 'POST',
    body: JSON.stringify({ device_id: deviceId, device_type: deviceType }),
  });
};

/**
 * Get session data from backend
 */
export const getSession = async (
  sessionKey: string
): Promise<ApiResult<EmmaSessionData>> => {
  return apiCall<EmmaSessionData>(`/sessions/${sessionKey}`);
};

/**
 * Save a message to the backend session
 */
export const saveMessage = async (
  sessionKey: string,
  role: 'user' | 'emma',
  content: string,
  metadata?: Record<string, any>
): Promise<ApiResult<{ status: string; message_count: number }>> => {
  return apiCall(`/sessions/${sessionKey}/messages`, {
    method: 'POST',
    body: JSON.stringify({ role, content, metadata }),
  });
};

/**
 * Update session context state
 */
export const updateContext = async (
  sessionKey: string,
  contextUpdates: Record<string, any>
): Promise<ApiResult<{ status: string; context_state: Record<string, any> }>> => {
  return apiCall(`/sessions/${sessionKey}/context`, {
    method: 'PATCH',
    body: JSON.stringify({ context_updates: contextUpdates }),
  });
};

/**
 * Sync sessions for cross-device support
 */
export const syncSessions = async (
  deviceId: string
): Promise<ApiResult<EmmaSessionData[]>> => {
  return apiCall<EmmaSessionData[]>(`/sessions?device_id=${encodeURIComponent(deviceId)}`);
};

export default {
  createSession,
  getSession,
  saveMessage,
  updateContext,
  syncSessions,
  isBackendSessionEnabled,
};

